import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { TransferService } from '../../services/transfer.service';
import { DataTransfer, CreateTransferRequest, TransferStatus, UpdateTransferStatusRequest } from '../../types/transfer.types';
import { RootState } from '../index';

// Интерфейс состояния
interface TransferState {
  transfers: DataTransfer[];
  sentTransfers: DataTransfer[];
  receivedTransfers: DataTransfer[];
  currentTransfer: DataTransfer | null;
  isLoading: boolean;
  error: string | null;
}

// Начальное состояние
const initialState: TransferState = {
  transfers: [],
  sentTransfers: [],
  receivedTransfers: [],
  currentTransfer: null,
  isLoading: false,
  error: null,
};

// Async thunks
export const fetchAllTransfers = createAsyncThunk(
  'transfers/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await TransferService.getAllTransfers();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Ошибка загрузки передач');
    }
  }
);

export const fetchSentTransfers = createAsyncThunk(
  'transfers/fetchSent',
  async (_, { rejectWithValue }) => {
    try {
      const response = await TransferService.getSentTransfers();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Ошибка загрузки отправленных передач');
    }
  }
);

export const fetchReceivedTransfers = createAsyncThunk(
  'transfers/fetchReceived',
  async (_, { rejectWithValue }) => {
    try {
      const response = await TransferService.getReceivedTransfers();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Ошибка загрузки полученных передач');
    }
  }
);

export const fetchTransferById = createAsyncThunk(
  'transfers/fetchById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await TransferService.getTransferById(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Ошибка загрузки передачи');
    }
  }
);

export const createTransfer = createAsyncThunk(
  'transfers/create',
  async (transferData: CreateTransferRequest, { rejectWithValue }) => {
    try {
      const response = await TransferService.createTransfer(transferData);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Ошибка создания передачи');
    }
  }
);

export const updateTransferStatus = createAsyncThunk(
  'transfers/updateStatus',
  async ({ id, statusData }: { id: string; statusData: UpdateTransferStatusRequest }, { rejectWithValue }) => {
    try {
      const response = await TransferService.updateTransferStatus(id, statusData);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Ошибка обновления статуса передачи');
    }
  }
);

// Slice
const transferSlice = createSlice({
  name: 'transfers',
  initialState,
  reducers: {
    clearTransferError: (state) => {
      state.error = null;
    },
    clearCurrentTransfer: (state) => {
      state.currentTransfer = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all transfers
      .addCase(fetchAllTransfers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllTransfers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.transfers = action.payload;
      })
      .addCase(fetchAllTransfers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Fetch sent transfers
      .addCase(fetchSentTransfers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSentTransfers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.sentTransfers = action.payload;
      })
      .addCase(fetchSentTransfers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Fetch received transfers
      .addCase(fetchReceivedTransfers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchReceivedTransfers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.receivedTransfers = action.payload;
      })
      .addCase(fetchReceivedTransfers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Fetch transfer by ID
      .addCase(fetchTransferById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTransferById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentTransfer = action.payload;
      })
      .addCase(fetchTransferById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Create transfer
      .addCase(createTransfer.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createTransfer.fulfilled, (state, action) => {
        state.isLoading = false;
        state.transfers.unshift(action.payload);
        state.sentTransfers.unshift(action.payload);
      })
      .addCase(createTransfer.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Update transfer status
      .addCase(updateTransferStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateTransferStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        
        // Обновляем во всех списках
        const updateTransferInList = (list: DataTransfer[]) => {
          const index = list.findIndex(t => t.id === action.payload.id);
          if (index !== -1) {
            list[index] = action.payload;
          }
        };
        
        updateTransferInList(state.transfers);
        updateTransferInList(state.sentTransfers);
        updateTransferInList(state.receivedTransfers);
        
        // Обновляем текущую передачу, если она открыта
        if (state.currentTransfer && state.currentTransfer.id === action.payload.id) {
          state.currentTransfer = action.payload;
        }
      })
      .addCase(updateTransferStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearTransferError, clearCurrentTransfer } = transferSlice.actions;

export const selectAllTransfers = (state: RootState) => state.transfers.transfers;
export const selectSentTransfers = (state: RootState) => state.transfers.sentTransfers;
export const selectReceivedTransfers = (state: RootState) => state.transfers.receivedTransfers;
export const selectCurrentTransfer = (state: RootState) => state.transfers.currentTransfer;
export const selectTransferLoading = (state: RootState) => state.transfers.isLoading;
export const selectTransferError = (state: RootState) => state.transfers.error;

export default transferSlice.reducer;